import { useCallback, useContext } from "react";
import {
  PheonixPushAction,
  SendMessagePheonixResponse,
} from "@web-src/modules/chats/types";
import {
  ChatType,
  ChatMessagePayloadAttachment,
  ChatMessagePayload,
  ChatMessageModel,
  ChatMessagePayloadPushType,
  ChatMessagePayloadType,
  ChatMessageType,
  ChatMessageAttachmentType,
} from "@jugl-web/rest-api";
import { PhoenixSocketContext } from "@web-src/modules/socket/providers/PhoenixSocketContext";
import { useSelector } from "react-redux";
import { v4 as uuid4 } from "uuid";
import { selectUserId } from "@web-src/features/auth/authSlice";
import {
  getFileSizeByUrl,
  getVideoDimensionsByUrl,
  getImageDimensionsByUrl,
  useTranslations,
  getAudioWaveform,
} from "@jugl-web/utils";
import { useTusUpload } from "@jugl-web/domain-resources/files/hooks/useTusUpload";
import { FilesModule } from "@jugl-web/domain-resources/files/types";
import { useUpdateChatMessages } from "@web-src/modules/chats/hooks/useUpdateChatMessages";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useConversations } from "@web-src/modules/chats/hooks/useConversations";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import {
  getAttachmentTypeByMimeType,
  messageToConversationLastMessage,
} from "@web-src/modules/chats/utils";
import { getAudioDuration } from "@jugl-web/utils/utils/files";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { clearAttachmentsLocalAttributes } from "../utils/clearAttachmentsLocalAttributes";

type SendMessageProps = {
  to: string;
  body?: string;
  clientMsgId?: string;
  type: ChatMessageType;
  extraPayload?: Partial<ChatMessageModel["payload"]>;
  files?: {
    file: File;
    caption?: string;
  }[];
  replyToMessage?: Pick<ChatMessageModel, "from" | "msg_id"> & {
    payload: Pick<
      ChatMessageModel["payload"],
      "body" | "conference" | "attachments"
    >;
  };
  forwarded?: boolean;
  gif?: string;
  attachmentType?: ChatMessageAttachmentType;
  conference?: ChatMessagePayload["conference"];
  isResend?: boolean;
};

type SendMessageResponseType =
  | "success"
  | "error"
  | "timeout"
  | "not-allowed"
  | "file-upload-error";

interface SendMessageResponse {
  type: SendMessageResponseType;
  message?: Partial<ChatMessageModel>;
  retryFileUpload?: () => void;
  resend?: (timeout: number) => void;
  error?: unknown;
}

export const useSendMessage = () => {
  const { channel } = useContext(PhoenixSocketContext);
  const { entity, showWriteRestrictedAlert, subscriptionInfo } =
    useEntityProvider();
  const meId = useSelector(selectUserId);
  const { profile } = useMe();
  const { addMessages, changeMessageId, updateMessage, deleteMessage } =
    useUpdateChatMessages();
  const { conversations, updateItem } = useConversations();
  const { uploadFile } = useTusUpload({
    entityId: entity?.id,
    module: FilesModule.attachment,
  });
  const { t } = useTranslations();

  const sendMessage = useCallback(
    async (props: SendMessageProps): Promise<SendMessageResponse> => {
      if (!subscriptionInfo?.actionsAllowed) {
        showWriteRestrictedAlert();
        return { type: "not-allowed" };
      }

      const isOnline = navigator.onLine;

      const {
        to,
        body,
        type,
        extraPayload,
        clientMsgId,
        files,
        replyToMessage,
        gif,
        attachmentType,
        conference,
        isResend,
      } = props;
      const chat = conversations.find((item) => item.id === to)?.data;
      const chatName =
        chat?.type === ChatType.chat ? profile?.displayName || "" : chat?.title;

      if (!entity?.id) {
        throw new Error("No active entity");
      }
      if (!channel) {
        throw new Error("No channel");
      }

      const attachments: ChatMessagePayloadAttachment[] = [];
      if (files) {
        const promises = files.map(async ({ file, caption }) => {
          const attachmentUrl = URL.createObjectURL(file);
          const fileType =
            attachmentType || getAttachmentTypeByMimeType(file.type);

          const amps =
            fileType === ChatMessageAttachmentType.voice
              ? await getAudioWaveform(file)
              : undefined;

          const attachment: ChatMessagePayloadAttachment = {
            type: fileType,
            name: file.name,
            size: file.size,
            amps,
            mimetype: file.type,
            caption,
            uid: "",
            _progress: 0,
            _local_url: URL.createObjectURL(file),
          };

          if (file.type.startsWith("image")) {
            const dimensions = await getImageDimensionsByUrl(attachmentUrl);
            attachment.width = dimensions.width;
            attachment.height = dimensions.height;
          } else if (file.type.startsWith("video")) {
            const dimensions = await getVideoDimensionsByUrl(attachmentUrl);
            attachment.width = dimensions.width;
            attachment.height = dimensions.height;
          } else if (file.type.startsWith("audio")) {
            try {
              attachment.duration = await getAudioDuration(file);
            } catch (error) {
              // skip
            }
          }

          return attachment;
        });

        const resolvedAttachments = await Promise.all(promises);
        attachments.push(...resolvedAttachments);
      }
      if (gif) {
        attachments.push({
          url: gif,
          type: ChatMessageAttachmentType.gifDirectUrl,
          name: "gif",
          size: await getFileSizeByUrl(gif),
          mimetype: "image/gif",
          ...(await getImageDimensionsByUrl(gif)),
          uid: "",
        });
      }

      const tempMsgId = clientMsgId || `temp-${uuid4()}`;
      const messageToSend: Partial<ChatMessageModel> = {
        to,
        from: meId,
        type,
        entity_id: entity?.id,
        payload: {
          version: CHAT_MESSAGE_PAYLOAD_VERSION,
          push_type: ChatMessagePayloadPushType.silent,
          title:
            chatName ||
            t({
              id: "chats-page.new-message",
              defaultMessage: "New Message",
            }),
          type: ChatMessagePayloadType.message,
          body,
          attachments: attachments.length ? attachments : undefined,
          ...extraPayload,
          client_msg_id: tempMsgId,
        },
      };

      if (replyToMessage && messageToSend.payload) {
        messageToSend.payload.reply_attributes_map = {
          reply_msg_id: replyToMessage.msg_id,
          reply_msg_body: replyToMessage.payload.body,
          reply_msg_user: replyToMessage.from,
          reply_conference: replyToMessage.payload.conference,
          reply_attachments: replyToMessage.payload.attachments
            ? clearAttachmentsLocalAttributes(
                replyToMessage.payload.attachments
              )
            : undefined,
        };
      }

      if (conference && conference.channel && messageToSend.payload) {
        messageToSend.payload.conference = conference;
      }
      if (chat?.id && messageToSend?.payload?.type !== "call") {
        if (isResend) {
          deleteMessage({
            chatId: chat.id,
            itemId: tempMsgId,
          });
        }
        addMessages({
          chatId: chat.id,
          skipIfNotInitialized: true,
          itemsToAdd: [
            {
              id: tempMsgId,
              data: {
                // Copy to be able to modify inner objects later
                ...JSON.parse(JSON.stringify(messageToSend)),
                timestamp: new Date().toISOString().replace("T", " "),
                is_read_by_self: true,
                _error: false,
                _pending: true,
              },
            },
          ],
        });
      }

      const uploadPromises = (files || []).map(async ({ file }, idx) => {
        try {
          const uploadResult = await uploadFile({
            file,
            addPath: true,
            onProgress: (bytesSent, bytesTotal) => {
              if (!chat?.id || !messageToSend.payload?.attachments) return;

              const progress = Math.round((bytesSent / bytesTotal) * 100);

              const updatedAttachments = messageToSend.payload.attachments.map(
                (attachment, index) => {
                  if (index !== idx) return { ...attachment };
                  return { ...attachment, _progress: progress };
                }
              );

              messageToSend.payload.attachments = updatedAttachments;

              updateMessage({
                chatId: chat.id,
                merge: true,
                item: {
                  id: tempMsgId,
                  data: {
                    payload: {
                      client_msg_id: tempMsgId,
                      attachments: updatedAttachments,
                    },
                  },
                },
              });
            },
          });

          const fileResponse = await uploadResult.promise;

          if (
            messageToSend.payload?.attachments &&
            messageToSend.payload.attachments[idx]?.type !==
              ChatMessageAttachmentType.gifDirectUrl
          ) {
            const updatedAttachments = messageToSend.payload.attachments.map(
              (attachment, index) => {
                if (index !== idx) return { ...attachment };
                return {
                  ...attachment,
                  url: fileResponse.url,
                  uid: fileResponse.uid,
                };
              }
            );

            messageToSend.payload.attachments = updatedAttachments;
          }

          return undefined;
        } catch {
          if (chat?.id && messageToSend.payload?.attachments) {
            const erroredAttachments = messageToSend.payload.attachments.map(
              (attachment, index) => {
                if (index !== idx) return { ...attachment };
                return { ...attachment, _error: true };
              }
            );

            messageToSend.payload.attachments = erroredAttachments;

            updateMessage({
              chatId: chat.id,
              merge: true,
              item: {
                id: tempMsgId,
                data: {
                  payload: {
                    client_msg_id: tempMsgId,
                    attachments: erroredAttachments,
                  },
                  _pending: false,
                  _error: true,
                },
              },
            });
          }

          return {
            type: "file-upload-error",
            message: messageToSend,
            retryFileUpload: () =>
              sendMessage({ ...props, clientMsgId: tempMsgId }),
            resend: message.resend,
          } as SendMessageResponse;
        }
      });

      const results = await Promise.all(uploadPromises);

      const uploadError = results.find(Boolean);

      if (uploadError) {
        return uploadError;
      }

      if (!isOnline) {
        return new Promise((resolve) => {
          if (chat?.id) {
            updateMessage({
              chatId: chat.id,
              merge: true,
              item: {
                id: tempMsgId,
                data: { _pending: false, _error: true },
              },
            });
            resolve({
              type: "error",
              message: messageToSend,
            });
          }
        });
      }
      if (messageToSend.payload?.attachments) {
        // Remove local stuff
        messageToSend.payload.attachments = clearAttachmentsLocalAttributes(
          messageToSend.payload.attachments
        );
      }
      const message = channel.push(PheonixPushAction.transport, messageToSend);

      return new Promise((resolve) => {
        message.receive("ok", (e: SendMessagePheonixResponse) => {
          if (chat?.id) {
            updateItem(
              {
                id: chat.id,
                data: {
                  lastMessage: messageToConversationLastMessage(
                    messageToSend as ChatMessageModel
                  ),
                },
              },
              true
            );
            changeMessageId({
              chatId: chat.id,
              oldId: tempMsgId,
              newId: e.msg_id,
            });
          }
          if (chat?.id) {
            updateMessage({
              chatId: chat.id,
              merge: true,
              item: {
                id: e.msg_id,
                data: { msg_id: e.msg_id, _pending: false, _error: false },
              },
            });
          }
          resolve({
            type: "success",
            message: messageToSend,
            resend: message.resend,
          });
        });
        const handleError = () => {
          if (chat?.id) {
            updateMessage({
              chatId: chat.id,
              merge: true,
              item: {
                id: tempMsgId,
                data: { _pending: false, _error: true },
              },
            });
          }
        };
        message.receive("error", (error) => {
          handleError();
          resolve({
            type: "error",
            error,
            message: messageToSend,
            resend: message.resend,
          });
        });
        message.receive("timeout", (error) => {
          handleError();
          resolve({
            type: "timeout",
            error,
            message: messageToSend,
            resend: message.resend,
          });
        });
      });
    },
    [
      deleteMessage,
      t,
      entity,
      conversations,
      channel,
      meId,
      showWriteRestrictedAlert,
      addMessages,
      uploadFile,
      updateMessage,
      updateItem,
      changeMessageId,
      profile,
      subscriptionInfo?.actionsAllowed,
    ]
  );
  return sendMessage;
};
